import { IRevenueAndSobHierarchyRow } from '../revenueAndSobHierarchy'

import { getFinancialAchievementScore } from './getFinancialAchievementScore'
import { getSobAchievementScore } from './getSobAchievementScore'

/** One node of the Revenue & SOB tree with its Achievement scores alongside its figures — what the
 *  table's two SCORE columns read. The scores hang off the row rather than replacing the figures
 *  because both are on screen at once, in the same group. The hierarchy-shaped counterpart of
 *  `IAchievementScoreCategoryRow`, which is what feeds the view switcher's tab badge. */
export interface IScoredRevenueAndSobHierarchyRow extends IRevenueAndSobHierarchyRow {
  salesScore: number | null
  sobScore: number | null
}

/** Scores every node of the hierarchy from its own aggregate figures, using the same two scoring
 *  functions `getAchievementScoreRows` applies to the flat category rows — and, since that one
 *  scores its Total row the same way, the root here and the badge built from that Total row agree.
 *
 *  No node averages its children, and none should. A node in this tree can have children on both
 *  axes at once (a Geo node opens into both its sub-geos and its own product subtree), so "the
 *  average of the children" has no single well-defined meaning. Scoring each node against its own
 *  baseline/plan/actuals is the one rule that reads the same at every level.
 *
 *  Both scores come out `null` for a period that is still open, since `buildFigures` nulls
 *  `actuals` in that case — the same condition under which the table hides its SCORE columns. */
export const getScoredHierarchyRows = (
  rows: IRevenueAndSobHierarchyRow[]
): IScoredRevenueAndSobHierarchyRow[] =>
  rows.map((row) => ({
    ...row,
    salesScore: getFinancialAchievementScore(row.sales.actuals, row.sales.plan),
    sobScore: getSobAchievementScore(row.sob.actuals, row.sob.plan, row.sob.baseline),
  }))
